import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import SkeletonList from '../../../../../components/MUIComponents/SkeletonList';
import FilterLayout from '../../../../../layouts/FilterLayout';
import { useThemeContext } from '../../../../../theme/ThemeContextProvider';
import { errorHelper } from '../../../../../utils/helper/error-helper';
import CheckBox from './components/CheckBox';

interface ICandidatesWorkExperienceFilterProps {
  isLoading: boolean;
  isError: boolean;
  label: string;
  error: FetchBaseQueryError | SerializedError | undefined;
  onShowMore: () => void;
  setActiveAthlete: (data: boolean) => void;
  activeAthlete: boolean;
  totalJobTitlesCount: number;
  jobTitles: string[];
  handleJobTitleChange: (data: string) => void;
  selectedJobTitles: string[];
  handleSearchChange: (data: string) => void;
  resetTrigger: boolean;
}

const CandidatesWorkExperienceFilter = ({
  isLoading,
  isError,
  label,
  error,
  onShowMore,
  activeAthlete,
  setActiveAthlete,
  totalJobTitlesCount,
  jobTitles,
  handleJobTitleChange,
  selectedJobTitles,
  handleSearchChange,
  resetTrigger,
}: ICandidatesWorkExperienceFilterProps) => {
  const { theme } = useThemeContext();
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSearchText('');
  }, [resetTrigger]);

  if (isLoading) {
    return (
      <Box
        sx={{ p: '22px 16px 11px 16px' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SkeletonList />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography sx={{ p: '22px 16px 11px 16px' }} color="error" variant="h4">
        {errorHelper(error)}
      </Typography>
    );
  }

  return (
    <FilterLayout name={label}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.375rem',
          padding: '0 1rem 1.375rem',
          width: '100%',
        }}
      >
        <FormGroup>
          <CheckBox
            label={'Active athlete'}
            checked={activeAthlete}
            onChange={() => setActiveAthlete(!activeAthlete)}
          />
        </FormGroup>

        <Box
          width={'100%'}
          height={'1px'}
          sx={{ backgroundColor: `${theme.palette.background.lightDark}` }}
        />

        <Box display={'flex'} flexDirection={'column'} gap={'1.375rem'}>
          <Typography variant="badge2" marginTop={'0.7rem'}>
            Title
          </Typography>

          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              handleSearchChange(e.target.value.trim());
            }}
            sx={searchFieldStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ p: 0 }}>
                  <SearchIcon sx={iconSizes} />
                </InputAdornment>
              ),
            }}
          />

          <FormGroup>
            {jobTitles?.map((jobTitle) => (
              <CheckBox
                key={jobTitle}
                label={jobTitle}
                checked={selectedJobTitles.includes(jobTitle)}
                onChange={() => handleJobTitleChange(jobTitle)}
              />
            ))}
          </FormGroup>

          {jobTitles && jobTitles.length < totalJobTitlesCount && (
            <Typography
              onClick={onShowMore}
              variant="h4"
              component="h4"
              color={'text.accent'}
              sx={{ marginTop: '10px', cursor: 'pointer' }}
            >
              + Show more
            </Typography>
          )}
        </Box>
      </Box>
    </FilterLayout>
  );
};

export default CandidatesWorkExperienceFilter;

const searchFieldStyles = {
  width: '100%',
  maxWidth: { md: '16rem', lg: '20.125rem' },
  '& .MuiInputBase-root': { height: '3.125rem', padding: '0.75rem 1rem' },
  '& .MuiOutlinedInput-input': { height: '3.125rem !important', padding: 0 },
  '& .MuiSvgIcon-root': { backgroundColor: 'transparent' },
};

const iconSizes = { width: '1.25rem', height: '1.25rem' };
